import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Slider from 'react-slick';
import { useSwipeable } from 'react-swipeable';
import LazyDisplayItem from '@/common/LazyDisplayItem';
import DeviceContext from '~/context/common/DeviceContext';
import CarouselContext from '~/context/vdp/CarouselContext';
import { GalleryImage } from '~/contracts/vdp';
import { useOptimizeImage, usePreventVerticalScroll } from '~/hooks/common';
import useGalleryImages from '~/hooks/vdp/useGalleryImages';
import BackArrow from './BackArrow';
import { EventName, GalleryType, width as widthConstants } from './constants';
import useSliderConfig from './hooks/useSliderItemConfig';
import NextArrow from './NextArrow';
import { CarouselItem, Wrapper } from './TileCarousel.styles';
import { CarouselConfig } from './types';

const useOffsetGalleryImages = ({
  cellSpacing = 0,
  imgWidth = 0
}): GalleryImage[] => {
  const images = useGalleryImages();
  const {
    screenSize: { width }
  } = useContext(DeviceContext);
  const slidesOnScreen = Math.round(width / (imgWidth + cellSpacing));
  const offset = slidesOnScreen > 3 ? 2 : 1;

  return useMemo(
    () => images.slice(offset).concat(images.slice(0, offset)),
    [images, offset]
  );
};

// TODO: This is not especially accurate, should be improved
const getIsImageInViewFn = (
  slideIndex: number,
  { slidesToScroll = 1 }: Partial<CarouselConfig>
) => {
  const slidesOnScreen = Math.floor(slidesToScroll / 2);
  const start = slideIndex - slidesOnScreen;
  const end = slideIndex + slidesOnScreen;

  return (index: number) => start <= index && end >= index;
};

const TileCarousel = () => {
  const { trackEvent } = useContext(AnalyticsContext);
  const { isFullScreen, openFullScreen, setSlideImgId, slideImgId } =
    useContext(CarouselContext);
  const optimizeImage = useOptimizeImage();
  const sliderRef = useRef<Slider>(null);
  const sliderWrapperRef = useRef<HTMLElement>(null);
  const sliderConfig = useSliderConfig();
  const images = useOffsetGalleryImages(sliderConfig);
  usePreventVerticalScroll(sliderWrapperRef);
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      trackEvent('VDP - Gallery - Swiped Left on Image Carousel');
    },
    onSwipedRight: () => {
      trackEvent('VDP - Gallery - Swiped Right on Image Carousel');
    },
    trackTouch: true,
    trackMouse: true
  });

  // NOTE: This will be inaccurate if we have to concat the image array to
  // produce a minimum number of images
  const slideIndex = (index => (index === -1 ? 0 : index))(
    images.findIndex(({ id }) => id === slideImgId)
  );
  const [slideIndexToShow, setSlideIndexToShow] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const onPrev = useCallback(() => {
    sliderRef?.current?.slickPrev();
  }, []);
  const onNext = useCallback(() => {
    sliderRef?.current?.slickNext();
  }, []);
  const isImageInView = getIsImageInViewFn(slideIndex, sliderConfig);

  useEffect(() => {
    if (isFullScreen) return;
    setSlideIndexToShow(slideIndex);
  }, [isFullScreen, slideIndex]);

  useEffect(() => {
    if (!isFullScreen) {
      sliderRef?.current?.slickGoTo(slideIndex);
    }
  }, [isFullScreen, slideIndex]);

  if (images.length === 0) return null;
  const minSlidesToLoad = 3;
  const scrollMultiplier = 3;
  const { imgWidth = widthConstants.desktop, slidesToScroll = 3 } =
    sliderConfig;

  return (
    <Wrapper {...swipeHandlers} data-qa="tile-carousel" ref={sliderWrapperRef}>
      <BackArrow onClick={onPrev} />
      <Slider
        ref={sliderRef}
        adaptiveHeight
        arrows={false}
        afterChange={index => {
          if (isFullScreen) return;
          const currentImage = images[index];
          setSlideImgId(currentImage.id);
          trackEvent(EventName.galleryEngaged, {
            galleryType: GalleryType.Tile,
            imageType: currentImage.type
          });
          setIsSwiping(false);
        }}
        {...sliderConfig}
        onSwipe={() => {
          setIsSwiping(true);
        }}
      >
        {images.map(({ id, imageUrl, type }, index) => {
          const optimizedImage = optimizeImage(imageUrl, imgWidth);

          return optimizedImage ? (
            <LazyDisplayItem
              key={imageUrl}
              offsetToLoad={Math.max(
                slidesToScroll * scrollMultiplier,
                minSlidesToLoad
              )}
              totalItems={images.length}
              visibleSlideIndex={slideIndexToShow}
              indexToDisplay={index}
            >
              <Clickable
                src={optimizedImage}
                onClick={() => {
                  if (isSwiping) return;
                  openFullScreen(id, type);
                }}
                element={CarouselItem}
                tabIndex={isImageInView(index) ? 0 : -1}
                data-analytics-id={null}
                data-qa="carousel-item"
              />
            </LazyDisplayItem>
          ) : null;
        })}
      </Slider>
      <NextArrow onClick={onNext} />
    </Wrapper>
  );
};

export default memo(TileCarousel);
